import React from "react";
import { Instagram, Facebook } from "lucide-react";
import { optimized } from "./img";
import { useContent } from "../../ContentContext";

const DEFAULT_HERO = {
    src: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/afh6pm5w_IMG_5929.jpg",
    label: "Spencer Rice At Strake",
    alt: "Spencer Rice visits Strake Sandwich Co",
};

const DEFAULT_PHOTOS = [
    { src: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/itbmgtd6_IMG_6799.jpeg", label: "Randy From Trailer Park Boys", alt: "Randy from Trailer Park Boys visits Strake Sandwich Co" },
    { src: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/ovdozhdn_IMG_5939.jpg", label: "Ed The Sock At Strake", alt: "Ed The Sock visits Strake Sandwich Co" },
    { src: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/b6xwxb4f_IMG_5931.jpg", label: "Built Daily", alt: "The Strake spread" },
    { src: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/gxiua2hn_IMG_5930.jpg", label: "Made To Order", alt: "Sandwich line behind the counter" },
    { src: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/5y9qpywg_IMG_5940.jpg", label: "Ahren Belisle Visits Strake", alt: "Ahren Belisle visits Strake Sandwich Co" },
];

const Tile = ({ photo, i, hero: isHero = false }) => (
    <div
        className={`relative overflow-hidden ${isHero ? "aspect-square" : "aspect-square"} border border-[#1A1A1A]/10 group`}
        data-testid={`gallery-photo-${i}`}
    >
        <img
            src={optimized(photo.src, { w: isHero ? 900 : 600, q: 80 })}
            alt={photo.alt}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-[1800ms] group-hover:scale-[1.04]"
        />
        {/* Bottom gradient for label legibility */}
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/70 via-black/15 to-transparent pointer-events-none" />
        {/* Editorial label */}
        <div className={`absolute ${isHero ? "bottom-4 left-5 right-5 gap-3" : "bottom-3 left-3 right-3 gap-2"} flex items-center`}>
            <div className={`h-px ${isHero ? "w-6" : "w-4"} bg-[#D4A437]`} />
            <div className={`font-body uppercase font-semibold text-[#F5F2EB] [text-shadow:_0_1px_3px_rgba(0,0,0,0.7)] truncate ${isHero ? "text-[12px] sm:text-[13px] tracking-[0.32em]" : "text-[10px] sm:text-[11px] tracking-[0.3em]"}`}>
                {photo.label}
            </div>
        </div>
    </div>
);

export const Gallery = () => {
    const { content } = useContent();
    const g = content?.gallery || {};
    // Photos in admin are stored under `photos`. First item acts as hero, rest become grid.
    const adminPhotos = (g.photos && g.photos.length > 0) ? g.photos.map((p) => ({
        src: p.src,
        label: p.caption || p.label || "",
        alt: p.caption || p.label || "Strake gallery photo",
    })) : null;

    const hero = adminPhotos ? adminPhotos[0] : DEFAULT_HERO;
    const photos = adminPhotos ? adminPhotos.slice(1) : DEFAULT_PHOTOS;
    const kicker = g.kicker || "Follow Along";
    const heading = g.title || "From The Socials.";
    const instagramUrl = content?.location?.instagram || "https://instagram.com/strakesandwichco";
    const facebookUrl = content?.location?.facebook || "https://facebook.com/strakesandwichco";
    const handle = g.handle || "@strakesandwichco";
    return (
        <section
            data-testid="gallery-section"
            className="bg-[#EBE2D2] py-12 px-6 relative grain overflow-hidden"
        >
            <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                    <div className="h-[2px] w-8 bg-[#C65A2C]" />
                    <div className="font-body text-[10px] uppercase tracking-[0.45em] font-semibold text-[#C65A2C]">
                        {kicker}
                    </div>
                    <div className="h-[2px] flex-grow bg-[#1A1A1A]/15" />
                </div>

                <h2 className="font-display text-4xl sm:text-5xl uppercase font-bold leading-[1] tracking-tight whitespace-nowrap text-[#1A1A1A]">
                    {heading}
                </h2>

                <div className="mt-5 flex items-center gap-3 flex-wrap">
                    <a
                        data-testid="instagram-link"
                        href={instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[#1A1A1A] hover:text-[#C65A2C] transition-colors"
                        aria-label="Instagram"
                    >
                        <Instagram size={15} strokeWidth={1.8} />
                        <span className="font-display uppercase text-[11px] tracking-[0.28em] font-bold">
                            Instagram
                        </span>
                    </a>
                    <span className="text-[#1A1A1A]/30 text-xs">•</span>
                    <a
                        data-testid="facebook-link"
                        href={facebookUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[#1A1A1A] hover:text-[#C65A2C] transition-colors"
                        aria-label="Facebook"
                    >
                        <Facebook size={15} strokeWidth={1.8} />
                        <span className="font-display uppercase text-[11px] tracking-[0.28em] font-bold">
                            Facebook
                        </span>
                    </a>
                </div>

                <div
                    data-testid="instagram-handle-link"
                    className="mt-3 inline-block border-b-2 border-[#1A1A1A] pb-1 font-display uppercase text-sm tracking-[0.25em] font-bold text-[#1A1A1A]"
                >
                    {handle}
                </div>

                <div className="mt-8 mb-6 h-px bg-[#1A1A1A]/20" />

                <div className="mb-2 shadow-[0_18px_40px_-20px_rgba(0,0,0,0.35)]">
                    <Tile photo={hero} i="hero" hero />
                </div>

                <div className="grid grid-cols-2 gap-2">
                    {photos.map((p, i) => (
                        <div key={i} className={i === photos.length - 1 && photos.length % 2 === 1 ? "col-span-2" : ""}>
                            <Tile photo={p} i={i} />
                        </div>
                    ))}
                </div>

                <div className="mt-8 h-px bg-[#1A1A1A]/15" />
            </div>
        </section>
    );
};
